import { useEffect, useCallback, useRef } from "react";
import { GOOGLE_CLIENT_ID } from "../../config/config";

// Development mode configuration - still use process.env for dev overrides
const IS_DEV_MODE = process.env.REACT_APP_ENV === "development";
const DEV_EMAIL = process.env.REACT_APP_DEV_EMAIL;
const DEV_NAME = process.env.REACT_APP_DEV_NAME;

// Check if dev override is enabled (must have both env var set and be in dev mode)
const USE_DEV_OVERRIDE = IS_DEV_MODE && DEV_EMAIL && DEV_NAME;

const GoogleSSO = ({ setGoogleUser, disabled = false }) => {
  const googleButtonRef = useRef(null);
  const googleInitialized = useRef(false);

  const decodeJwtResponse = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  };

  // Handle dev override login - use email from environment variables
  const handleDevLogin = () => {
    if (disabled) return;

    const userData = {
      name: DEV_NAME,
      email: DEV_EMAIL,
      picture: null,
      credential: null
    };
    setGoogleUser(userData);
  };

  const handleCredentialResponse = useCallback(
    (response) => {
      try {
        // Decode the JWT token to get user information
        const decodedToken = decodeJwtResponse(response.credential);

        // Accept any valid Google account - this is a multi-tenant platform
        const userData = {
          name: decodedToken.name,
          email: decodedToken.email,
          picture: decodedToken.picture,
          credential: response.credential
        };

        setGoogleUser(userData);
      } catch (error) {
        console.error("Error processing Google response:", error);
        alert("Failed to process Google sign-in. Please try again.");
      }
    },
    [setGoogleUser]
  );

  useEffect(() => {
    // Skip Google SDK initialization if using dev override
    if (USE_DEV_OVERRIDE || disabled || googleInitialized.current) return;

    const initializeGoogleSignIn = () => {
      if (!window.google || !googleButtonRef.current) return;

      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: false
        });

        // Render Google button - use "icon" type for just the logo or custom button
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin", // Just shows "Sign in" instead of "Sign in with Google"
          shape: "rectangular",
          logo_alignment: "center",
          width: 160
        });

        googleInitialized.current = true;
      } catch (error) {
        console.error("Error initializing Google Sign-In:", error);
      }
    };

    const loadGoogleScript = () => {
      const existingScript = document.querySelector(
        'script[src="https://accounts.google.com/gsi/client"]'
      );
      if (existingScript) {
        if (window.google) {
          initializeGoogleSignIn();
        } else {
          existingScript.addEventListener("load", initializeGoogleSignIn);
        }
        return;
      }

      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      script.onerror = () =>
        console.error("Failed to load Google Sign-In script");
      document.head.appendChild(script);
    };

    loadGoogleScript();
  }, [handleCredentialResponse, disabled]);

  // If dev override is enabled, render a custom button with the configured email
  if (USE_DEV_OVERRIDE) {
    return (
      <button
        type="button"
        onClick={handleDevLogin}
        disabled={disabled}
        className={`flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-lg transition-colors ${
          disabled
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-gray-50 cursor-pointer"
        }`}
        title={`Dev mode: Will use ${DEV_EMAIL}`}
      >
        <img src="/Google.svg" alt="Google" className="w-5 h-5" />
        <span className="text-sm font-medium text-gray-700">Google</span>
      </button>
    );
  }

  // In production or when dev override is not set, render Google's official OAuth button
  return (
    <div
      ref={googleButtonRef}
      className={`flex items-center justify-center ${
        disabled ? "opacity-50 pointer-events-none" : ""
      }`}
      style={{ minHeight: "44px", minWidth: "160px" }}
    />
  );
};

export default GoogleSSO;
